import { Box, Typography, Skeleton, Divider, Stack, Button } from '@mui/material';
import { useGetApiV5AccountsAccountIdScheduleK1s } from '@liscio/api';
import { ScheduleK1sArraySchema } from '@liscio/api';
import { usePostApiV5AccountAccountIdTaxReturnsZipPackage } from '@liscio/api';
import React, { useMemo } from 'react';
import { K1Members } from 'components/Accounts/elements/components/K1Members/K1Members';
import toast from 'react-hot-toast';
import { TOAST_ARIA_PROPS } from 'utilities/utils';

type K1PackagesProps = {
  accountUuid: string | undefined;
};

type K1sGroupedByYearData = {
  tax_year: number;
  members: ScheduleK1sArraySchema;
}[];

export const K1Packages = ({ accountUuid }: K1PackagesProps) => {
  const { data, isLoading, isError } = useGetApiV5AccountsAccountIdScheduleK1s(
    accountUuid || '',
    {},
    {
      query: { enabled: Boolean(accountUuid) },
    },
  );

  const K1sGroupedByYear = useMemo(
    () =>
      data &&
      data
        .reduce((acc, k1) => {
          const existingYear = acc.find(
            (year) => year.tax_year === k1.tax_year,
          );
          if (existingYear) {
            existingYear.members.push(k1);
          } else {
            acc.push({ tax_year: k1.tax_year, members: [k1] });
          }
          return acc;
        }, [] as K1sGroupedByYearData)
        ?.sort((a, b) => b.tax_year - a.tax_year),
    [data],
  );

  const { mutateAsync: postZipDownload } =
    usePostApiV5AccountAccountIdTaxReturnsZipPackage();

  const handleDownloadAll = (year: number) => {
    postZipDownload(
      {
        data: {
          tax_year: year,
        },
        accountId: accountUuid || '',
      },
      {
        onSuccess: () => {
          // Stays until clicked, so the user sees it before the download is ready.
          toast.success(
            (t) => (
              <Box
                component="span"
                onClick={() => toast.dismiss(t.id)}
                sx={{ cursor: 'pointer' }}
              >
                Preparing download. You will recieve a notification when the
                download is ready.
              </Box>
            ),
            { duration: Infinity, ariaProps: TOAST_ARIA_PROPS },
          );
        },
        onError: (error) => {
          toast.error(
            "We weren't able to create the file you requested. Please try again.",
            { ariaProps: TOAST_ARIA_PROPS },
          );
          console.error(
            'K1packages.tsx handleDownloadAll error',
            accountUuid,
            year,
            error,
          );
        },
      },
    );
  };

  if (isError) {
    return <Typography mt={2.5}>Error loading K1 packages</Typography>;
  }

  if (isLoading) {
    return <Skeleton height={300} data-testid="skeleton" />;
  }

  if (!data?.length) {
    return null;
  }

  return (
    <Stack gap={0.625} mt={3.75}>
      <Typography variant="h3">K1 Packages</Typography>
      <Stack
        p={1.25}
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      />
      <Stack p={0.625}>
        {K1sGroupedByYear?.map((k1) => (
          <React.Fragment key={k1.tax_year}>
            <Stack direction="row" justifyContent="space-between">
              <Typography
                variant="h6"
                fontWeight="bold"
                component="span"
                py={2.5}
              >
                {k1.tax_year}
              </Typography>
              <Button onClick={() => handleDownloadAll(k1.tax_year)}>
                Download All
              </Button>
            </Stack>
            <Divider />
            {k1.members.map((member) => (
              <K1Members key={member.uuid} member={member} />
            ))}
          </React.Fragment>
        ))}
      </Stack>
    </Stack>
  );
};
